import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "../apiAdmin";

// Admin "Costs" tab — the company's vendor ledger (what Request App pays).
//
// Plan prices come from server/lib/companyCostsCatalog.js (changed by PR);
// what was actually paid comes from the weekly Gmail receipt sync
// (POST /admin/costs/receipts). This component only renders GET /admin/costs.
//
// Company bills are in USD, so this tab formats USD only — talent/fan money
// elsewhere in the dashboard stays per-currency.

type Cadence = "monthly" | "yearly" | "usage" | "one-time";
type Status = "verified" | "estimate" | "free" | "pending" | "paid" | "uncatalogued";
type View = "monthly" | "yearly";

interface Receipt {
  _id: string;
  vendor: string;
  amount: number;
  currency: string;
  paidAt: string;
  period: string;
  ref?: string | null;
  source?: string;
  note?: string;
}

interface CostRow {
  vendor: string;
  name: string;
  purpose: string;
  plan?: string;
  cadence: Cadence;
  expected: number;
  expectedMax?: number;
  growsWith?: string;
  account?: string;
  status: Status;
  note?: string;
  when?: string;
  monthlyEq: number;
  monthlyEqMax: number;
  nextRenewal: string | null;
  latestReceipt: Receipt | null;
  receipts: Receipt[];
}

interface CostsResponse {
  rows: CostRow[];
  totals: { monthlyLow: number; monthlyHigh: number; annualLow: number; annualHigh: number; vendors: number };
  nextRenewal: { vendor: string; name: string; date: string; amount: number } | null;
  lastSyncedAt: string | null;
  generatedAt: string;
}

const usd = (n: number, decimals = 2) =>
  `$${n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

// "$20.00", or "$5–$20" when the catalog only knows a range.
function usdRange(low: number, high: number, decimals = 2): string {
  if (Math.abs(high - low) < 0.005) return usd(low, decimals);
  return `${usd(low, 0)}–${usd(high, 0)}`;
}

function fmtDate(d?: string | null, withYear = false): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString([], withYear ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

function fmtReceipt(r: Receipt): string {
  return r.currency && r.currency !== "USD" ? `${r.amount.toFixed(2)} ${r.currency}` : usd(r.amount);
}

type Tone = "green" | "amber" | "grey" | "red";

// A live receipt always wins over whatever the catalog assumed.
function statusPill(r: CostRow): { tone: Tone; text: string } {
  if (r.latestReceipt) return { tone: "green", text: `receipt ${fmtDate(r.latestReceipt.paidAt)}` };
  switch (r.status) {
    case "verified":
      return { tone: "green", text: "verified" };
    case "paid":
      return { tone: "green", text: "paid" };
    case "free":
      return { tone: "grey", text: "free tier" };
    case "pending":
      return { tone: "amber", text: r.note || "pending" };
    case "uncatalogued":
      return { tone: "red", text: "not in catalog" };
    default:
      return { tone: "amber", text: r.note || "estimate" };
  }
}

function Pill({ tone, text, title }: { tone: Tone; text: string; title?: string }) {
  const cls = tone === "green" ? "" : ` adm-pill-${tone}`;
  return (
    <span className={`pill adm-pill-sm${cls}`} title={title}>
      {text}
    </span>
  );
}

function receiptHistory(r: CostRow): string | undefined {
  if (!r.receipts.length) return r.note || undefined;
  const lines = r.receipts.map((x) => `${x.period}: ${fmtReceipt(x)}`);
  return [r.note, ...lines].filter(Boolean).join("\n");
}

export default function AdminCostsTab() {
  const [data, setData] = useState<CostsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState<View>("monthly");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get<CostsResponse>("/costs");
      setData(res.data);
      setError("");
    } catch {
      setError("Couldn’t load costs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const recurring = useMemo(() => (data?.rows || []).filter((r) => r.cadence !== "one-time"), [data]);
  const oneTime = useMemo(() => (data?.rows || []).filter((r) => r.cadence === "one-time"), [data]);
  const mult = view === "monthly" ? 1 : 12;

  if (loading && !data) {
    return (
      <section className="fade-in">
        <div className="adm-card muted-text">Loading costs…</div>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="fade-in">
        <div className="adm-card muted-text">
          {error || "No data."}{" "}
          <button className="adm-btn-light" onClick={load}>
            Retry
          </button>
        </div>
      </section>
    );
  }

  const { totals, nextRenewal, lastSyncedAt } = data;

  return (
    <section className="fade-in adm-tab-container">
      {error && (
        <div
          className="adm-red adm-fs-78" style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px" }}
        >
          Couldn’t refresh — showing the last loaded numbers.
        </div>
      )}

      {/* KPI trio */}
      <div className="adm-kpi-trio adm-mb-0">
        <div className="kpi-card kpi-card-compact">
          <div className="kpi-icon green-bg adm-green" style={{ fontWeight: 800 }}>$</div>
          <div>
            <div className="kpi-label">Monthly burn</div>
            <div className="kpi-value">{usdRange(totals.monthlyLow, totals.monthlyHigh, 0)}</div>
            <div className="kpi-sublabel">tools + hosting, {totals.vendors} vendors</div>
          </div>
        </div>
        <div className="kpi-card kpi-card-compact">
          <div className="kpi-icon blue-bg" style={{ color: "var(--adm-blue)", fontWeight: 800 }}>Y</div>
          <div>
            <div className="kpi-label">Annualized</div>
            <div className="kpi-value">{usdRange(totals.annualLow, totals.annualHigh, 0)}</div>
            <div className="kpi-sublabel">incl. yearly renewals</div>
          </div>
        </div>
        <div className="kpi-card kpi-card-compact">
          <div className="kpi-icon orange-bg adm-orange" style={{ fontWeight: 800 }}>!</div>
          <div>
            <div className="kpi-label">Next renewal</div>
            <div className="kpi-value">{nextRenewal ? fmtDate(nextRenewal.date) : "—"}</div>
            <div className="kpi-sublabel">{nextRenewal ? `${nextRenewal.name} · ${usd(nextRenewal.amount)}` : "no dated renewals"}</div>
          </div>
        </div>
      </div>

      {/* Recurring */}
      <div className="adm-card" style={{ padding: "14px 16px" }}>
        <div className="adm-mb-8" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <h3 className="adm-section-title">Recurring</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div className="adm-costs-seg" role="group" aria-label="Amount period">
              <button type="button" className={view === "monthly" ? "active" : ""} onClick={() => setView("monthly")}>
                Monthly
              </button>
              <button type="button" className={view === "yearly" ? "active" : ""} onClick={() => setView("yearly")}>
                Yearly
              </button>
            </div>
            <button className="adm-btn-light" onClick={load} disabled={loading}>
              {loading ? "Refreshing…" : "↻ Refresh"}
            </button>
          </div>
        </div>
        <div className="adm-table-wrapper">
          <table className="adm-table-light adm-table-compact">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>For</th>
                <th>Plan</th>
                <th className="adm-costs-num">{view === "monthly" ? "Monthly" : "Yearly"}</th>
                <th className="adm-costs-num">Last receipt</th>
                <th>Renews</th>
                <th>Grows with…</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recurring.map((r) => {
                const pill = statusPill(r);
                return (
                  <tr key={r.vendor} className="hover-row">
                    <td>
                      <span className="adm-semibold">{r.name}</span>
                      {r.account && <span className="adm-costs-sub">{r.account}</span>}
                    </td>
                    <td>{r.purpose}</td>
                    <td>{r.plan || "—"}</td>
                    <td className="adm-costs-num">{usdRange(r.monthlyEq * mult, r.monthlyEqMax * mult)}</td>
                    <td className="adm-costs-num">{r.latestReceipt ? fmtReceipt(r.latestReceipt) : "—"}</td>
                    <td>{r.nextRenewal ? fmtDate(r.nextRenewal) : r.cadence === "yearly" ? "yearly" : "—"}</td>
                    <td className="muted-text">{r.growsWith || "—"}</td>
                    <td>
                      <Pill tone={pill.tone} text={pill.text} title={receiptHistory(r)} />
                    </td>
                  </tr>
                );
              })}
              <tr className="adm-costs-total">
                <td colSpan={3}>Total</td>
                <td className="adm-costs-num">
                  {view === "monthly"
                    ? usdRange(totals.monthlyLow, totals.monthlyHigh, 0)
                    : usdRange(totals.annualLow, totals.annualHigh, 0)}
                </td>
                <td colSpan={4} className="muted-text" style={{ fontWeight: 500 }}>
                  yellow rows are estimates until a receipt is logged
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* One-time / pending */}
      <div className="adm-card" style={{ padding: "14px 16px" }}>
        <h3 className="adm-section-title adm-mb-8">One-time / pending</h3>
        <div className="adm-table-wrapper">
          <table className="adm-table-light adm-table-compact">
            <thead>
              <tr>
                <th>Item</th>
                <th>For</th>
                <th className="adm-costs-num">Amount</th>
                <th>When</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {oneTime.map((r) => {
                const pill = statusPill(r);
                return (
                  <tr key={r.vendor} className="hover-row">
                    <td className="adm-semibold">{r.name}</td>
                    <td>{r.purpose}</td>
                    <td className="adm-costs-num">{usd(r.expected)}</td>
                    <td>{r.when || (r.latestReceipt ? fmtDate(r.latestReceipt.paidAt, true) : "—")}</td>
                    <td>
                      <Pill tone={pill.tone} text={pill.text} title={receiptHistory(r)} />
                    </td>
                  </tr>
                );
              })}
              {oneTime.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted-text">Nothing pending.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="muted-text adm-fs-72 adm-mt-8 adm-mb-0">
          Receipts sync from Gmail every Monday · last sync {lastSyncedAt ? fmtDate(lastSyncedAt, true) : "never"} · plan prices update through Claude.
        </p>
      </div>
    </section>
  );
}
